import type {
  CvEntry,
  CvModel,
  CvSection,
} from "@/lib/tool-kit/admissions/academic-cv";
import { escapeHtml } from "@/lib/tool-kit/citation-parts";

/**
 * The four files an Academic CV leaves the page as: Word-compatible HTML, Markdown,
 * LaTeX and plain text. Every one is drawn from the same `CvModel`, so they say the
 * same thing; only the markup differs.
 *
 * Inline marks in a line: `*italic*` (kept as typed) and the writer's own name, set in
 * bold wherever it appears in a reference.
 */

export interface Run {
  text: string;
  italic: boolean;
  bold: boolean;
}

const ITALIC_SPAN = /\*([^*\n]+)\*/g;
const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

function splitBold(text: string, boldName: string): Run[] {
  const name = boldName.trim();
  if (name === "") {
    return [{ text, italic: false, bold: false }];
  }
  const pattern = new RegExp(`(${name.replace(REGEX_SPECIAL, "\\$&")})`, "gi");
  return text
    .split(pattern)
    .filter((part) => part !== "")
    .map((part) => ({
      text: part,
      italic: false,
      bold: part.toLowerCase() === name.toLowerCase(),
    }));
}

/** A line as runs: `*italic*` spans, and the writer's name in bold. */
export function toRuns(text: string, boldName: string): Run[] {
  const runs: Run[] = [];
  let cursor = 0;
  for (const match of text.matchAll(ITALIC_SPAN)) {
    const at = match.index ?? 0;
    if (at > cursor) {
      runs.push(...splitBold(text.slice(cursor, at), boldName));
    }
    runs.push(
      ...splitBold(match[1], boldName).map((run) => ({ ...run, italic: true }))
    );
    cursor = at + match[0].length;
  }
  if (cursor < text.length) {
    runs.push(...splitBold(text.slice(cursor), boldName));
  }
  return runs;
}

function mapRuns(
  text: string,
  boldName: string,
  render: (run: Run) => string
): string {
  return toRuns(text, boldName).map(render).join("");
}

/* ------------------------------------------------------------------- text */

function numberLabel(entry: CvEntry): string {
  return entry.number === undefined ? "" : `${entry.number}. `;
}

function entryToText(entry: CvEntry, section: CvSection): string[] {
  const plain = mapRuns(entry.text, "", (run) => run.text);
  const lines: string[] = [];
  if (section.kind === "dated" && entry.date !== "") {
    lines.push(`${entry.date}  ${plain}`);
  } else {
    lines.push(`${numberLabel(entry)}${plain}`);
  }
  for (const detail of entry.details) {
    lines.push(`  - ${mapRuns(detail, "", (run) => run.text)}`);
  }
  return lines;
}

export function cvToText(model: CvModel): string {
  const { header } = model;
  const out: string[] = [header.name.toUpperCase()];
  for (const line of [header.title, header.affiliation, ...header.contacts]) {
    if (line !== "") {
      out.push(line);
    }
  }
  for (const section of model.sections) {
    out.push(
      "",
      section.heading.toUpperCase(),
      "-".repeat(section.heading.length)
    );
    for (const entry of section.entries) {
      out.push(...entryToText(entry, section));
    }
  }
  return `${out.join("\n")}\n`;
}

/* --------------------------------------------------------------- markdown */

function mdRun(run: Run): string {
  let text = run.text;
  if (run.italic) {
    text = `*${text}*`;
  }
  return run.bold ? `**${text}**` : text;
}

function entryToMarkdown(
  entry: CvEntry,
  section: CvSection,
  boldName: string
): string[] {
  const body = mapRuns(entry.text, boldName, mdRun);
  let first: string;
  if (section.kind === "paragraph") {
    first = body;
  } else if (section.kind === "dated" && entry.date !== "") {
    first = `- **${entry.date}** — ${body}`;
  } else if (entry.number === undefined) {
    first = `- ${body}`;
  } else {
    first = `${entry.number}. ${body}`;
  }
  return [
    first,
    ...entry.details.map((detail) => `  - ${mapRuns(detail, boldName, mdRun)}`),
  ];
}

export function cvToMarkdown(model: CvModel, boldName: string): string {
  const { header } = model;
  const out: string[] = [`# ${header.name}`, ""];
  const identity = [header.title, header.affiliation].filter(
    (line) => line !== ""
  );
  if (identity.length > 0) {
    out.push(identity.join(" — "), "");
  }
  if (header.contacts.length > 0) {
    out.push(header.contacts.join(" · "), "");
  }
  for (const section of model.sections) {
    out.push(`## ${section.heading}`, "");
    for (const entry of section.entries) {
      out.push(...entryToMarkdown(entry, section, boldName));
    }
    out.push("");
  }
  return `${out.join("\n").trimEnd()}\n`;
}

/* ------------------------------------------------------------------ latex */

const LATEX_ESCAPES: Record<string, string> = {
  "\\": "\\textbackslash{}",
  "&": "\\&",
  "%": "\\%",
  $: "\\$",
  "#": "\\#",
  _: "\\_",
  "{": "\\{",
  "}": "\\}",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
};
const LATEX_SPECIAL = /[\\&%$#_{}~^]/g;
const EN_DASH = /–/g;
const EM_DASH = /—/g;

export function latexEscape(text: string): string {
  return text
    .replace(LATEX_SPECIAL, (char) => LATEX_ESCAPES[char] ?? char)
    .replace(EM_DASH, "---")
    .replace(EN_DASH, "--");
}

function texRun(run: Run): string {
  let text = latexEscape(run.text);
  if (run.italic) {
    text = `\\textit{${text}}`;
  }
  return run.bold ? `\\textbf{${text}}` : text;
}

function texLines(
  entries: CvEntry[],
  section: CvSection,
  boldName: string
): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    const body = mapRuns(entry.text, boldName, texRun);
    let item = `\\item ${body}`;
    if (section.kind === "dated" && entry.date !== "") {
      item = `\\item[${latexEscape(entry.date)}] ${body}`;
    } else if (entry.number !== undefined) {
      item = `\\item[${entry.number}.] ${body}`;
    }
    out.push(item);
    if (entry.details.length > 0) {
      out.push(
        "\\begin{itemize}[nosep]",
        ...entry.details.map((d) => `\\item ${mapRuns(d, boldName, texRun)}`),
        "\\end{itemize}"
      );
    }
  }
  return out;
}

const HANGING =
  "[label={},labelsep=0pt,labelwidth=0pt,leftmargin=1.5em,itemindent=-1.5em,nosep]";
const DATED_LIST =
  "[leftmargin=3.6cm,labelsep=0.4cm,labelwidth=3.2cm,align=left,nosep]";
const NUMBERED_LIST = "[leftmargin=1.2cm,labelsep=0.2cm,align=left,nosep]";

/** The list environment for a section: dated and numbered entries carry labels. */
function texList(section: CvSection): { env: string; options: string } {
  if (section.entries.some((entry) => entry.date !== "")) {
    return { env: "description", options: DATED_LIST };
  }
  if (section.entries.some((entry) => entry.number !== undefined)) {
    return { env: "description", options: NUMBERED_LIST };
  }
  return { env: "itemize", options: HANGING };
}

function texSection(section: CvSection, boldName: string): string[] {
  const out = [`\\section*{${latexEscape(section.heading)}}`];
  if (section.kind === "paragraph") {
    out.push(mapRuns(section.entries[0]?.text ?? "", boldName, texRun));
    return out;
  }
  const { env, options } = texList(section);
  out.push(
    `\\begin{${env}}${options}`,
    ...texLines(section.entries, section, boldName),
    `\\end{${env}}`
  );
  return out;
}

export function cvToLatex(model: CvModel, boldName: string): string {
  const { header } = model;
  const subtitle = [header.title, header.affiliation]
    .filter((line) => line !== "")
    .map(latexEscape)
    .join(" \\\\ ");
  const contact = header.contacts.map(latexEscape).join(" \\quad ");
  const out = [
    "\\documentclass[11pt]{article}",
    "\\usepackage[utf8]{inputenc}",
    "\\usepackage[T1]{fontenc}",
    "\\usepackage[margin=2.2cm]{geometry}",
    "\\usepackage{enumitem}",
    "\\pagestyle{empty}",
    "\\setlength{\\parindent}{0pt}",
    "\\begin{document}",
    `\\begin{center}{\\LARGE\\bfseries ${latexEscape(header.name)}}${subtitle ? `\\\\[4pt] ${subtitle}` : ""}${contact ? `\\\\[4pt] {\\small ${contact}}` : ""}\\end{center}`,
  ];
  for (const section of model.sections) {
    out.push("", ...texSection(section, boldName));
  }
  out.push("", "\\end{document}");
  return `${out.join("\n")}\n`;
}

/* ------------------------------------------------------ word-compatible html */

function htmlRun(run: Run): string {
  let html = escapeHtml(run.text);
  if (run.italic) {
    html = `<i>${html}</i>`;
  }
  return run.bold ? `<b>${html}</b>` : html;
}

function htmlInline(text: string, boldName: string): string {
  return mapRuns(text, boldName, htmlRun);
}

function htmlDetails(entry: CvEntry, boldName: string): string {
  if (entry.details.length === 0) {
    return "";
  }
  const items = entry.details
    .map((detail) => `<li>${htmlInline(detail, boldName)}</li>`)
    .join("");
  return `<ul style="margin:2pt 0 2pt 0">${items}</ul>`;
}

function htmlDatedRows(section: CvSection, boldName: string): string {
  const rows = section.entries
    .map(
      (entry) =>
        `<tr><td style="width:1.3in;vertical-align:top;padding:2pt 8pt 2pt 0"><b>${escapeHtml(entry.date)}</b></td><td style="vertical-align:top;padding:2pt 0">${htmlInline(entry.text, boldName)}${htmlDetails(entry, boldName)}</td></tr>`
    )
    .join("");
  return `<table style="border-collapse:collapse;width:100%">${rows}</table>`;
}

function htmlReferences(section: CvSection, boldName: string): string {
  return section.entries
    .map(
      (entry) =>
        `<p style="margin:0 0 4pt 0.4in;text-indent:-0.4in">${escapeHtml(numberLabel(entry))}${htmlInline(entry.text, boldName)}</p>`
    )
    .join("");
}

function htmlSection(section: CvSection, boldName: string): string {
  const heading = `<h2 style="font-size:12pt;margin:14pt 0 4pt 0;border-bottom:1px solid #444;text-transform:uppercase">${escapeHtml(section.heading)}</h2>`;
  if (section.kind === "paragraph") {
    return `${heading}<p style="margin:0">${htmlInline(section.entries[0]?.text ?? "", boldName)}</p>`;
  }
  if (section.kind === "dated") {
    const dated = section.entries.some((entry) => entry.date !== "");
    return `${heading}${dated ? htmlDatedRows(section, boldName) : htmlReferences(section, boldName)}`;
  }
  return `${heading}${htmlReferences(section, boldName)}`;
}

/** The CV's body markup: what "copy" puts on the clipboard for pasting into Word. */
export function cvToHtmlBody(model: CvModel, boldName: string): string {
  const { header } = model;
  const identity = [header.title, header.affiliation]
    .filter((line) => line !== "")
    .map(
      (line) => `<p style="margin:0;text-align:center">${escapeHtml(line)}</p>`
    )
    .join("");
  const contact =
    header.contacts.length > 0
      ? `<p style="margin:2pt 0 0 0;text-align:center;font-size:10pt">${header.contacts.map(escapeHtml).join(" &middot; ")}</p>`
      : "";
  return [
    `<h1 style="font-size:20pt;margin:0;text-align:center">${escapeHtml(header.name)}</h1>`,
    identity,
    contact,
    ...model.sections.map((section) => htmlSection(section, boldName)),
  ].join("");
}

/** A complete document Word opens directly (save the file with a .doc extension). */
export function cvToWordHtml(model: CvModel, boldName: string): string {
  return [
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">',
    '<head><meta charset="utf-8">',
    `<title>${escapeHtml(model.header.name)}</title>`,
    "<style>@page{size:8.5in 11in;margin:1in}body{font-family:'Times New Roman',serif;font-size:11pt}</style>",
    "</head><body>",
    cvToHtmlBody(model, boldName),
    "</body></html>",
  ].join("\n");
}
